import { Descriptions, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type TtbPaymentModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/TtbPaymentModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import TtbPaymentModuleActions from './TtbPaymentModuleActions';
import TtbPaymentSettingsEmptyList from './TtbPaymentSettingsEmptyList';
import TtbPaymentSettingsTable from './TtbPaymentSettingsTable';

export type TtbPaymentModulePageProps = { module: TtbPaymentModuleWithPermissionsSpecsFragment };

const TtbPaymentModulePage = ({ module }: TtbPaymentModulePageProps) => {
    const { t } = useTranslation(['ttbPaymentModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('ttbPaymentModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('ttbPaymentModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<TtbPaymentModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={screens.md ? t('ttbPaymentModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <Divider>{t('ttbPaymentModuleDetails:dividers.mainDetails')}</Divider>
            {module.paymentSettings.length === 0 ? (
                <TtbPaymentSettingsEmptyList module={module} />
            ) : (
                <TtbPaymentSettingsTable module={module} />
            )}
        </ConsolePageWithHeader>
    );
};

export default TtbPaymentModulePage;
